import { useSyncExternalStore } from 'react';

/**
 * Interface languages of the cabinet and «Маяк». Russian is the source language: a Russian sentence is its own key,
 * so an untranslated string still reads correctly (a missing key falls back to the Russian text).
 * Dictionaries live in src/i18n/dict/*.ts and register themselves through i18n/index.ts.
 */
export type Lang = 'ru' | 'en' | 'uk';
export const LANGS: { code: Lang; label: string }[] = [
  { code: 'ru', label: 'RU' },
  { code: 'en', label: 'EN' },
  { code: 'uk', label: 'UA' },
];

type Dict = Record<string, string>;
const dicts: Record<Lang, Dict> = { ru: {}, en: {}, uk: {} };
const STORAGE_KEY = 'mo_lang';
const LOCALES: Record<Lang, string> = { ru: 'ru-RU', en: 'en-GB', uk: 'uk-UA' };

/** Called by src/i18n/index.ts for each dictionary file. */
export function registerDict(lang: Lang, dict: Dict): void {
  Object.assign(dicts[lang], dict);
}

function detect(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ru' || saved === 'en' || saved === 'uk') return saved;
  } catch {
    // Private mode or blocked storage: fall through to the browser language.
  }
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'ru').toLowerCase();
  if (nav.startsWith('uk')) return 'uk';
  if (nav.startsWith('en')) return 'en';
  return 'ru';
}

let current: Lang = detect();
const listeners = new Set<() => void>();

export function currentLang(): Lang {
  return current;
}

/** Locale for Intl formatting of dates and numbers. */
export function currentLocale(): string {
  return LOCALES[current];
}

export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Not persisted: the choice lasts for this page only.
  }
  document.documentElement.lang = lang;
  listeners.forEach((l) => l());
}

/** Translate a Russian source string. Placeholders: t('Найдено {n}', { n: 3 }). */
export function t(text: string, vars?: Record<string, string | number>): string {
  const raw = dicts[current][text] ?? text;
  return vars ? raw.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] === undefined ? m : String(vars[k]))) : raw;
}

/** Subscribes the component to language changes; the app re-mounts its router on change (see main.tsx). */
export function useLang(): Lang {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => current,
  );
}

document.documentElement.lang = current;
